import { useEffect, useState } from 'react';

import { Modal, CloseButton } from 'react-bootstrap';

import ActivitySearch from '../../components/BenchmarkActivities/ActivitySearch';
import BenchmarkActivitiesTable from '../../components/BenchmarkActivities/BenchmarkActivitiesTable';
import BenchmarkErrorMessage from '../../components/BenchmarkActivities/BenchmarkErrorMessage';
import EditBenchmarkActivity from '../../components/BenchmarkActivities/EditBenchmarkActivity';
import LoadingWrapper from '../../components/LoadingWrapper';
import { errorToast, infoToast, MessageOptions } from '../../components/Toasty';

import { hasQualityAdminAccess } from '../../functions/AccessHelpers';

import {
    getActivities,
    unlinkTradeFromActivity,
    linkTradeToActivity,
    renameActivity,
    createActivity,
} from '../../services/Quality/BenchmarkActivities';
import { getTrades } from '../../services/Trades';

import EmployeeInfo from '../../types/Employee/EmployeeInfo'
import { Activity, EMPTY_ACTIVITY } from '../../types/Quality/Activity';
import { Trade } from '../../types/Quality/Trade';

interface BenchmarkActivitiesProps {
    userData: EmployeeInfo,
    isMocked: boolean,
};

const benchmarkErrorOptions: MessageOptions = {
    autoClose: false,
    pauseOnHover: true,
    customID: undefined,
    closeOnClick: false,
}

const BenchmarkActivitiesPage = ({
    userData,
    isMocked,
}: BenchmarkActivitiesProps) => {
    // Used for managing the loading wrapper
    const [activitiesLoading, setActivitesLoading] = useState<boolean>(false);
    const [activitiesLoadingMessage, setActivitesLoadingMessage] = useState<string>("Retrieving User Data");

    const [saving, setSaving] = useState<boolean>(false);

    // Used for updating the data from the user
    const [activityList, setActivityList] = useState<Activity[]>([]);
    const [allTradeList, setAllTradesList] = useState<Trade[]>([]);

    const [currentActivity, setCurrentActivity] = useState<Activity>(EMPTY_ACTIVITY);
    const [updatedActivity, setUpdatedActivity] = useState<Activity>(EMPTY_ACTIVITY);

    // Used for controlling Modal visibility
    const [showSaveCheckModal, setShowSaveCheckModal] = useState<boolean>(false);
    const [showEditModal, setShowEditModal] = useState<boolean>(false);
    const [isActivityActionEnabled, setIsActivityActionEnabled] = useState<boolean>(false);

    // Used for manually refreshing the UI
    const [tableKey, setTableKey] = useState<string>(`benchmarkActivityTable-${Date.now()}`);
    const [activityKey, setActivityKey] = useState<string>(`benchmarkActivity-${Date.now()}`);

    useEffect(() => {
        setActivitesLoading(true);
        /**
         * this will catch that the user data hasn't loaded yet saving the user
         *   from the issue where they are working an anything they have
         *   entered gets removed when the page refreshes
         */
        if (userData.employeeID === 0) {
            return;
        }

        let cancelled = false;

        /**
         * async function to allow the useEffect to be able to go get data
         */
        const getPageData = async () => {
            try {
                setActivitesLoadingMessage("Getting Activity Data");
                const [
                    ActivityListResponse,
                    TradeListResponse,
                ] = await Promise.all([getActivities(), getTrades(true, ['QUALITY', 'TRAINING'])]);

                if (cancelled) return;
                setActivityList(ActivityListResponse.data.items);
                setAllTradesList(TradeListResponse.data.items);
            } catch (error) {
                if (cancelled) return;
                const errorMsg = (
                    <BenchmarkErrorMessage
                        title={'Data Retrieval Error'}
                        error={JSON.stringify(error)}
                    >
                        <p>
                            There was an error downloading the selected data necessary for the page.
                            Refresh and try again or contact Blattner Support for assistance.
                        </p>
                    </BenchmarkErrorMessage>
                );
                errorToast(errorMsg, benchmarkErrorOptions);
            } finally {
                if (!cancelled) {
                    setActivitesLoadingMessage('');
                    setActivitesLoading(false);
                }
            }
        };

        // call the function to get the data for the page
        getPageData();

        return () => { cancelled = true; };
    }, [userData, setActivitesLoadingMessage,  setActivitesLoading]);

    const handleAddActivityClick = (newActivityName: string) => {
        setCurrentActivity({
            ...EMPTY_ACTIVITY,
            name: newActivityName,
        });
        setShowSaveCheckModal(false);
        setShowEditModal(true);
    }

    const handleEditClick = (activityId: number) => {
        if (!activityId) {
            return;
        }
        const activity = activityList.find((a) => a.id === activityId) ?? EMPTY_ACTIVITY
        setCurrentActivity(activity);
        setShowEditModal(true);
    }

    /**
     * Handle all the steps necessary to create a new Activity and link trades (if any)
     * @param newActivity
     */
    const saveNewActivity = async (newActivity: Activity) => {
        try {
            // create a new Activity
            const { data: { activityId, newRowVersion } } = await createActivity(newActivity.name, userData.email);

            // setup the new activity variables
            newActivity.id = activityId;
            newActivity.rowVersion = newRowVersion;
            newActivity.isActive = true;

            // link any trades that are associated
            await Promise.all(
                newActivity.trades.map(t => linkTradeToActivity(newActivity.id, t.id, userData.email)),
            );

            /**
             * update everywhere the activity is represented in order to
             *   keep the rowVersion up to date
             */
            setUpdatedActivity(newActivity);
            setCurrentActivity(newActivity);

            const newList = [newActivity, ...activityList];
            setActivityList(newList);

            // update the currentActivity, updatedActivity and Activity List

        } catch (error) {
            throw error;
        }
    }

    /**
     * No params here because the save action is internal to the page
     * @returns nothing
     */
    const onAddClick = async () => {
        if (isMocked) {
            infoToast('You have clicked on the "Add" button.  This has been disabled because you are currently not yourself.')
            return;
        }
        if (updatedActivity.id !== EMPTY_ACTIVITY.id) {
            console.log("something is very wrong while Adding a new activity");
            return
        }

        setSaving(true);
        try {
            // save the updated activity to the dB
            await saveNewActivity({ ...updatedActivity });
        } catch (error) {
            const errorMsg = (
                <BenchmarkErrorMessage
                    title={'Save Error'}
                    error={JSON.stringify(error)}
                >
                    <p>
                        There was an error saving the new activity.
                        Refresh and try again or contact Blattner Support for assistance.
                    </p>
                </BenchmarkErrorMessage>
            );
            errorToast(errorMsg, benchmarkErrorOptions);
        } finally {
            // stop the spinner
            // refresh the table
            setTableKey(`benchmarkActivityTable-${Date.now()}`);
            setActivityKey(`benchmarkActivity-${Date.now()}`);
            setSaving(false);
        }
    };

    /**
     * This will handle making the update to the list of trades we are
     *   displaying as well as sending the data to the API for storage
     * @param newActivity this is the "updated activity"
     * @param oldActivity this is the "current activity"
     * @returns 
     */
    const saveExistingActivityUpdates = async (newActivity: Activity, oldActivity: Activity) => {
        newActivity.name = newActivity.name.toUpperCase();
        /**
         * get trade differences between the old and the new.  This will be
         *  used to (un)link trades respectively
         */
        const oldTradeIds = new Set(oldActivity.trades.map(t => t.id));
        const newTradeIds  = new Set(newActivity.trades.map(t => t.id));

        // present in updated but not in old → link these
        const toLink = newActivity.trades.filter(t => !oldTradeIds.has(t.id));

        // present in old but not in updated → unlink these
        const toUnlink = oldActivity.trades.filter(t => !newTradeIds.has(t.id));

        try {
            /**
             * just to be sure, catch if there are no changes from the old
             *   to prevent needles API calls
             */
            if (toLink.length === 0 && toUnlink.length === 0 && (oldActivity.name === newActivity.name))
            {
                return;
            }

            /**
             * run all the (un)linking at once
             */
            await Promise.all([
                ...toUnlink.map(t => unlinkTradeFromActivity(newActivity.id, t.id, userData.email)),
                ...toLink.map(t => linkTradeToActivity(newActivity.id, t.id, userData.email)),
            ]);

            /**
             * if we are updating the name we need to update the activity row
             *   directly this is handled differently than just (un)linking
             *   folders, such that we need to track the concurrency variable
             *   "rowVersion" so the API knows that there isn't a data collision
             */
            if (oldActivity.name !== newActivity.name && !!newActivity.rowVersion) {
                const { data: { newRowVersion} } = await renameActivity(newActivity.id, newActivity.name, newActivity.rowVersion,  userData.email);

                /**
                 * update the rowVersion in the udpated activity so we can
                 *   make further updates if desired
                 */
                newActivity = {
                    ...newActivity,
                    name: newActivity.name,
                    rowVersion: newRowVersion,
                };

            }
            /**
             * update everywhere the activity is represented in order to
             *   keep the rowVersion up to date
             */
            setUpdatedActivity(newActivity);
            setCurrentActivity(newActivity);

            const newList = [...activityList];
            const index = newList.findIndex(i => i.id === newActivity.id);
            newList[index] = newActivity;
            setActivityList(newList);

        } catch (error) {
            /**
             * re-throwing the error here to be picked up at the level above this.
             */
            throw error;
        }
    };

    /**
     * No params here because the save action is internal to the page
     * @returns nothing
     */
    const onUpdateClick = async () => {
        if (isMocked) {
            infoToast('You have clicked on the "Save" button.  This has been disabled because you are currently not yourself.')
            return;
        }
        if(currentActivity.id === EMPTY_ACTIVITY.id || updatedActivity.id === EMPTY_ACTIVITY.id) {
            const errorMsg = (
                <div>
                    <p>
                        There has been a critical error!  Please refresh the page and try again.
                    </p>
                </div>
            );
            errorToast(errorMsg, benchmarkErrorOptions);
            return
        }

        // activate the spinner
        setSaving(true);
        try {
            // save the updated activity to the dB
            await saveExistingActivityUpdates({ ...updatedActivity }, { ...currentActivity });
        } catch (error) {
            const errorMsg = (
                <BenchmarkErrorMessage
                    title={'Edit Error'}
                    error={JSON.stringify(error)}
                >
                    <p>
                    There was an error updating the selected activity.
                    Refresh and try again or contact Blattner Support for assistance.
                    </p>
                </BenchmarkErrorMessage>
            );
            errorToast(errorMsg, benchmarkErrorOptions);
        } finally {
            // stop the spinner
            // refresh the table
            setTableKey(`benchmarkActivityTable-${Date.now()}`);
            setActivityKey(`benchmarkActivity-${Date.now()}`);
            setSaving(false);
        }
    };

    const activityNameFilled = (activity: Activity) => (activity.name ?? '').trim().length > 0;

    const tradesEqual = (a: Activity, b: Activity) => {
        const idsA = (a.trades ?? []).map(t => t.id).sort((x, y) => x - y);
        const idsB = (b.trades ?? []).map(t => t.id).sort((x, y) => x - y);
        if (idsA.length !== idsB.length) {
            return false;
        }
        return idsA.every((id, idx) => id === idsB[idx]);
    };

    useEffect(() => {
        if (!showEditModal) {
            setIsActivityActionEnabled(false);
            return;
        }

        const isNewActivity = currentActivity.id === EMPTY_ACTIVITY.id;
        if (isNewActivity) {
            const hasName = activityNameFilled(updatedActivity);
            const hasTrades = (updatedActivity.trades ?? []).length > 0;
            setIsActivityActionEnabled(hasName && hasTrades);
            return;
        }

        const nameChanged = (updatedActivity.name ?? '').trim() !== (currentActivity.name ?? '').trim();
        const tradesChanged = !tradesEqual(updatedActivity, currentActivity);
        const activeChanged = updatedActivity.isActive !== currentActivity.isActive;
        const lockedChanged = updatedActivity.isLocked !== currentActivity.isLocked;
        setIsActivityActionEnabled(nameChanged || tradesChanged || activeChanged || lockedChanged);
    }, [showEditModal, currentActivity, updatedActivity]);

    return (
        <div className='benchmark-activities-page' style={{width: '80vw'}}>
            <ActivitySearch
                showModal={showSaveCheckModal}
                activityList={activityList}
                onClose={() => setShowSaveCheckModal(false)}
                onAddClick={(name: string) => handleAddActivityClick(name.toUpperCase())}
            />
        <Modal
            show={showEditModal}
            keyboard={false}
        >
            <Modal.Header>
                <div className='col'>
                    <div className='row'>
                        <div className='col'>
                        </div>
                        <div className='col-9 h3 text-center'>
                            {
                                showEditModal &&
                                <>
                                    {
                                        currentActivity.id === EMPTY_ACTIVITY.id
                                        ? 'Add Benchmark Activity' : 'Edit Benchmark Activity'
                                    }
                                </>
                            }
                        </div>
                        <div className='col'>
                            <CloseButton
                                onClick={() => {setShowEditModal(false)}}
                            />
                        </div>
                    </div>
                </div>
            </Modal.Header>
            <Modal.Body>
                <LoadingWrapper showLoading={saving}>
                <div>
                    <EditBenchmarkActivity
                        key={activityKey}
                        activity={currentActivity}
                        allTrades={allTradeList}
                        onChange={(activity: Activity) => {
                            setUpdatedActivity(activity)
                        }}
                        userData={userData}
                    />
                </div>
                </LoadingWrapper>
            </Modal.Body>
            <Modal.Footer>
                <div className='col'>
                    <div className='row my-2'>
                        <div className='col-7'>
                        </div>
                        <div className='col-2'>
                            <button
                                className='btn btn-primary'
                                onClick={() => {currentActivity.id === EMPTY_ACTIVITY.id ? onAddClick() : onUpdateClick()}}
                                disabled={!isActivityActionEnabled}
                            >
                                {currentActivity.id === EMPTY_ACTIVITY.id ? 'Add' : 'Save'}
                            </button>
                        </div>
                        <div className='col-2'>
                            <div
                                className='btn btn-danger'
                                onClick={() => {setShowEditModal(false)}}
                            >
                                Close
                            </div>
                        </div>
                    </div>
                </div>
            </Modal.Footer>
        </Modal>
            <div className='row border-bottom mx-3'>
                <div className='col-2'>
                </div>
                <div className='col-6 text-center h2 my-3'>
                    Benchmark Activities
                </div>
                <div className='col-2 my-3'>
                </div>
                <div className='col-1 my-3'>
                    <button
                        title='Add a New Benchmark Activity'
                        className='btn btn-primary'
                        onClick={() => setShowSaveCheckModal(true)}
                    >
                        Add
                    </button>
                </div>
            </div>
            <div className='row pt-1 mt-1'>
                <LoadingWrapper
                    windSpinner
                    windSpinnerScale={3}
                    showLoading={activitiesLoading}
                    loadingMessage={activitiesLoadingMessage}
                >
                    <BenchmarkActivitiesTable
                        key={tableKey}
                        activityList={activityList}
                        onActivityEdit={(id: number) => {handleEditClick(id)}}
                        canEdit={hasQualityAdminAccess(userData)}
                    />
               </LoadingWrapper>
            </div>
        </div>
    );
};

export default BenchmarkActivitiesPage;